import { IonIcon } from "@ionic/react";
import "./styles/CustomButton.css";

interface ButtonProps {
  type?: "button" | "submit" | "reset";
  variant?: "link" | "button";
  href?: string;
  id?: string;
  style?: React.CSSProperties;
  className?: string;
  fill?: "solid" | "outline" | "clear" | "dashed";
  color?:
    | "primary"
    | "secondary"
    | "tertiary"
    | "success"
    | "warning"
    | "danger"
    | "light"
    | "medium"
    | "vypzee"
    | "black";
  size?: "small" | "default" | "large" | "icon";
  icon?: any;
  iconSize?: string;
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  disabled?: boolean;
  children?: React.ReactNode;
}

/**********************************************************************************
 * Name: Custom Button Component
 * Description: A custom button component which was created to replace the Ion
 *              Button Component by giving more flexibility
 **********************************************************************************/
const Button: React.FC<ButtonProps> = ({
  type = "button",
  variant = "button",
  href,
  id = "",
  style,
  className = "",
  fill = "solid",
  color = "vypzee",
  size = "default",
  icon,
  iconSize = "1.5rem",
  onClick,
  onMouseEnter,
  onMouseLeave,
  disabled = false,
  children,
}) => {
  // Based on the variant, the button will be rendered as a link or a button
  const Component = variant === "link" ? "a" : "button";
  const commonProps = {
    id,
    style,
    className: `${className} custom-button custom-button-${size} custom-button-${fill}-${color}`,
    onClick,
    onMouseEnter,
    onMouseLeave,
  };

  return (
    <Component
      {...commonProps}
      {...(variant === "link" ? { href } : { type, disabled })}
    >
      {!!icon && (
        <IonIcon
          icon={icon}
          style={{ marginTop: "-2px", fontSize: iconSize, flexShrink: 0 }}
        />
      )}
      {children}
    </Component>
  );
};

// Test component to test the buttons
const ButtonTest: React.FC = () => {
  return (
    <>
      <div style={{ display: "flex", gap: "25px" }}>
        <Button size="default" fill="solid">
          Visit Vypzee
        </Button>
        <Button size="default" fill="outline">
          Visit Vypzee
        </Button>
        <Button size="default" fill="clear">
          Visit Vypzee
        </Button>
        <Button size="default" fill="dashed">
          Visit Vypzee
        </Button>
      </div>
      <div style={{ display: "flex", gap: "25px" }}>
        <Button size="default" fill="solid" color="primary">
          Visit Vypzee
        </Button>
        <Button size="default" fill="outline" color="primary">
          Visit Vypzee
        </Button>
        <Button size="default" fill="clear" color="primary">
          Visit Vypzee
        </Button>
        <Button size="default" fill="dashed" color="primary">
          Visit Vypzee
        </Button>
      </div>
      <div style={{ display: "flex", gap: "25px" }}>
        <Button size="default" fill="solid" color="secondary">
          Visit Vypzee
        </Button>
        <Button size="default" fill="outline" color="secondary">
          Visit Vypzee
        </Button>
        <Button size="default" fill="clear" color="secondary">
          Visit Vypzee
        </Button>
        <Button size="default" fill="dashed" color="secondary">
          Visit Vypzee
        </Button>
      </div>
      <div style={{ display: "flex", gap: "25px" }}>
        <Button size="default" fill="solid" color="tertiary">
          Visit Vypzee
        </Button>
        <Button size="default" fill="outline" color="tertiary">
          Visit Vypzee
        </Button>
        <Button size="default" fill="clear" color="tertiary">
          Visit Vypzee
        </Button>
        <Button size="default" fill="dashed" color="tertiary">
          Visit Vypzee
        </Button>
      </div>
      <div style={{ display: "flex", gap: "25px" }}>
        <Button size="default" fill="solid" color="success">
          Visit Vypzee
        </Button>
        <Button size="default" fill="outline" color="success">
          Visit Vypzee
        </Button>
        <Button size="default" fill="clear" color="success">
          Visit Vypzee
        </Button>
        <Button size="default" fill="dashed" color="success">
          Visit Vypzee
        </Button>
      </div>
      <div style={{ display: "flex", gap: "25px" }}>
        <Button size="default" fill="solid" color="warning">
          Visit Vypzee
        </Button>
        <Button size="default" fill="outline" color="warning">
          Visit Vypzee
        </Button>
        <Button size="default" fill="clear" color="warning">
          Visit Vypzee
        </Button>
        <Button size="default" fill="dashed" color="warning">
          Visit Vypzee
        </Button>
      </div>
      <div style={{ display: "flex", gap: "25px" }}>
        <Button size="default" fill="solid" color="danger">
          Visit Vypzee
        </Button>
        <Button size="default" fill="outline" color="danger">
          Visit Vypzee
        </Button>
        <Button size="default" fill="clear" color="danger">
          Visit Vypzee
        </Button>
        <Button size="default" fill="dashed" color="danger">
          Visit Vypzee
        </Button>
      </div>
      <div style={{ display: "flex", gap: "25px" }}>
        <Button size="default" fill="solid" color="medium">
          Visit Vypzee
        </Button>
        <Button size="default" fill="outline" color="medium">
          Visit Vypzee
        </Button>
        <Button size="default" fill="clear" color="medium">
          Visit Vypzee
        </Button>
        <Button size="default" fill="dashed" color="medium">
          Visit Vypzee
        </Button>
      </div>
      <div style={{ display: "flex", gap: "25px" }}>
        <Button size="default" fill="solid" color="light">
          Visit Vypzee
        </Button>
        <Button size="default" fill="outline" color="light">
          Visit Vypzee
        </Button>
        <Button size="default" fill="clear" color="light">
          Visit Vypzee
        </Button>
        <Button size="default" fill="dashed" color="light">
          Visit Vypzee
        </Button>
      </div>
      <div style={{ display: "flex", gap: "25px" }}>
        <Button size="default" fill="solid" color="black">
          Visit Vypzee
        </Button>
        <Button size="default" fill="outline" color="black">
          Visit Vypzee
        </Button>
        <Button size="default" fill="clear" color="black">
          Visit Vypzee
        </Button>
        <Button size="default" fill="dashed" color="black">
          Visit Vypzee
        </Button>
      </div>
    </>
  );
};

export default Button;

export { ButtonTest };
